import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../api'

export type AkuteStatusValue =
  | 'not_enrolled'
  | 'pending'
  | 'synced'
  | 'note_pushed'
  | 'needs_resubmit'
  | 'signed_off'
  | 'error'

export interface AkuteStatusResponse {
  /** Whether the akuteIntegration flag is on for this user (server-evaluated). */
  enabled: boolean
  status?: AkuteStatusValue
  signed_off_at?: string | null
  /** Akute user id of the clinician the patient last chose (pre-selects the picker). */
  clinician_user_id?: string | null
  /** Display name of that clinician, persisted at send time (migration 049) so the
   *  note-status line can name the signer without an Akute round-trip. Null for
   *  members who enrolled before it was captured. */
  clinician_name?: string | null
  /** Whether the user has joined the PelviaCare+ early-access waitlist — per-account,
   *  so the "coming soon" card's confirmed state survives across devices. */
  waitlist_joined?: boolean
}

export interface AkuteEnrollResponse {
  success: boolean
  status: string
}

/** A clinician the patient can pick to author + sign their consult note. */
export interface AkuteClinician {
  id: string
  name: string
  npi?: string | null
  is_prescriber: boolean
}

export interface AkuteCliniciansResponse {
  clinicians: AkuteClinician[]
}

/** Shape of the 409 body when required demographics aren't on file yet. */
export interface AkuteMissingDemographics {
  error: 'missing_demographics'
  missing_fields: string[]
}

/** Pull the missing-demographics list off a thrown ApiError, if present. */
export function missingDemographicsFrom(err: unknown): string[] | null {
  if (err instanceof ApiError && err.status === 409) {
    const data = err.data as AkuteMissingDemographics | null
    if (data && Array.isArray(data.missing_fields)) return data.missing_fields
  }
  return null
}

/** Statuses that are still in flight — a clinician sign-off can land any moment,
 * so we poll while in one of these and stop once it's terminal. */
const _AKUTE_PENDING: AkuteStatusValue[] = ['synced', 'note_pushed']

export function useAkuteStatus() {
  return useQuery({
    queryKey: ['akute', 'status'],
    queryFn: () => api.get<AkuteStatusResponse>('/api/akute/status'),
    staleTime: 30_000,
    // Poll while the note is waiting on the clinician so the dashboard flips to
    // "Signed ✓" on its own when the sign-off webhook lands (no manual refresh).
    // Stops at signed_off / error / not_enrolled.
    refetchInterval: (query) =>
      _AKUTE_PENDING.includes(query.state.data?.status as AkuteStatusValue) ? 20_000 : false,
  })
}

/** Record that the user joined the PelviaCare+ waitlist (per-account memory).
 *  The card also posts to LaunchList directly; this persists the "joined" state
 *  server-side so the confirmed view shows on any device. */
export function useJoinPelviaCareWaitlist() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ waitlist_joined: boolean }>('/api/akute/waitlist', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['akute', 'status'] })
    },
  })
}

/** One section of the consult note preview. */
export interface AkuteNoteSection {
  title: string
  text: string
}

export interface AkuteNotePreviewResponse {
  service_date: string
  sections: AkuteNoteSection[]
}

/** Preview the exact consult note the send will push (paid members only). */
export function useAkuteNotePreview(enabled = true) {
  return useQuery({
    queryKey: ['akute', 'note', 'preview'],
    // Building the preview regenerates the Assessment & Plan via an LLM
    // server-side, which routinely outlives the client's default 20s hard cap —
    // the fetch was being aborted at exactly 20s while the server finished the
    // note anyway. Give it 90s (gunicorn allows 120) and don't auto-retry: every
    // attempt costs a full LLM generation.
    queryFn: () =>
      api.get<AkuteNotePreviewResponse>('/api/akute/note/preview', { timeoutMs: 90_000 }),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

/** The clinic's selectable clinicians, for the send-flow picker. */
export function useAkuteClinicians(enabled = true) {
  return useQuery({
    queryKey: ['akute', 'clinicians'],
    queryFn: () => api.get<AkuteCliniciansResponse>('/api/akute/clinicians'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

/** (Re)send the consult note, authored + signed by the chosen `clinicianId`. */
export function useAkuteEnroll() {
  const qc = useQueryClient()
  return useMutation({
    // The send rebuilds the consult note server-side (same LLM generation as
    // the preview), so it routinely runs 40–80s. On the default 20s cap the
    // client aborts and reports failure while the server finishes and pushes
    // the note anyway — the user retries and Akute ends up with duplicates.
    mutationFn: (clinicianId: string) =>
      api.post<AkuteEnrollResponse>(
        '/api/akute/enroll',
        { clinician_id: clinicianId },
        { timeoutMs: 90_000 },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['akute'] })
    },
  })
}

export interface AkuteDemographics {
  first_name: string
  last_name: string
  legal_sex: string
  /** ISO `YYYY-MM-DD` (what <input type="date"> emits; Akute accepts it as-is). */
  date_of_birth: string
  address_line_1: string
  address_line_2?: string
  address_city: string
  address_state: string
  address_zipcode: string
  primary_phone_number: string
  primary_phone_type?: string
}

/** Persist the demographics Akute needs (folded into intake settings_extended). */
export function useSaveAkuteDemographics() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fields: AkuteDemographics) =>
      api.post('/api/patient/intake', fields as unknown as Record<string, unknown>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] })
      qc.invalidateQueries({ queryKey: ['intake'] })
    },
  })
}
